/**
 * Move input notation, its parser, and the command table that turns a per-tick InputSnapshot
 * into a move id. Three-free.
 *
 * Grammar
 * -------
 *   input := [motion '+']? [direction '+']? button ('+' button)*
 *          | motion                       (movement-only commands like a dash)
 *          | direction                    (walk/crouch, handled by the state machine directly)
 *
 *   motion    := 'ff' | 'bb' | 'dd' | 'qcf' | 'qcb' | 'dp'
 *   direction := 'f' | 'b' | 'u' | 'd' | 'df' | 'db' | 'uf' | 'ub' | 'n'
 *   button    := '1' (LP) | '2' (HP) | '3' (LK) | '4' (HK) | '5' (SP) | 'g' (guard)
 *
 * Examples:  "1"  "2"  "f+2"  "d+3"  "df+4"  "ff+2"  "qcf+2"  "1+3" (throw)  "b+1+3"  "5"
 *
 * A string follow-up authors only the ADDITIONAL press — "2" for a jab-jab — and the parent
 * move's `cancels` list gates when it becomes available. That keeps the notation short and
 * makes the string tree the single source of truth for what follows what.
 */

import { COMMAND_WINDOW_TICKS, INPUT_BUFFER_TICKS } from '../core/constants.js';

export const MOTIONS = Object.freeze(['ff', 'bb', 'dd', 'qcf', 'qcb', 'dp']);
const DIRECTION_TO_NUMPAD = Object.freeze({
  n: 5, f: 6, b: 4, u: 8, d: 2, df: 3, db: 1, uf: 9, ub: 7,
});
const BUTTON_TOKENS = Object.freeze({ 1: 'lp', 2: 'hp', 3: 'lk', 4: 'hk', 5: 'sp', g: 'guard' });

/**
 * @typedef {object} Trigger
 * @property {string} motion   '' when none
 * @property {number} dir      numpad direction required, 5 = any/neutral
 * @property {boolean} dirRequired
 * @property {string[]} buttons resolved button field names, e.g. ['lp','lk']
 * @property {number} specificity higher wins when several triggers match
 */

/**
 * Parses a notation string into a Trigger.
 * @param {string} input
 * @param {string} moveId for error messages
 * @returns {Trigger}
 */
export function parseInput(input, moveId = '?') {
  const trigger = { motion: '', dir: 5, dirRequired: false, buttons: [], specificity: 0 };
  const raw = String(input || '').trim().toLowerCase();
  if (raw === '') return trigger;

  const tokens = raw.split('+').map((t) => t.trim()).filter(Boolean);
  for (let i = 0; i < tokens.length; i++) {
    const tok = tokens[i];
    if (MOTIONS.includes(tok)) {
      if (trigger.motion) throw new Error(`Move "${moveId}": input "${input}" has two motions.`);
      if (i !== 0) throw new Error(`Move "${moveId}": input "${input}" — the motion must come first.`);
      trigger.motion = tok;
      continue;
    }
    if (tok in DIRECTION_TO_NUMPAD) {
      if (trigger.dirRequired) throw new Error(`Move "${moveId}": input "${input}" has two directions.`);
      trigger.dir = DIRECTION_TO_NUMPAD[tok];
      trigger.dirRequired = tok !== 'n';
      continue;
    }
    if (tok in BUTTON_TOKENS) {
      const btn = BUTTON_TOKENS[tok];
      if (!trigger.buttons.includes(btn)) trigger.buttons.push(btn);
      continue;
    }
    throw new Error(
      `Move "${moveId}": input "${input}" has unknown token "${tok}". ` +
        `Legal: motions ${MOTIONS.join('/')}, directions ${Object.keys(DIRECTION_TO_NUMPAD).join('/')}, ` +
        `buttons ${Object.keys(BUTTON_TOKENS).join('/')}.`,
    );
  }

  // Specificity ranks how "hard to enter" a command is, so that pressing forward+HP never
  // resolves to the bare HP move when both are legal. Motions outrank directions outrank
  // extra buttons.
  trigger.specificity =
    (trigger.motion ? 100 : 0) + (trigger.dirRequired ? 20 : 0) + trigger.buttons.length * 3;
  return trigger;
}

/** Renders a Trigger back to notation. Used by MOVE_LIST.md generation and training mode. */
export function formatTrigger(trigger) {
  const parts = [];
  if (trigger.motion) parts.push(trigger.motion);
  if (trigger.dirRequired) {
    for (const k in DIRECTION_TO_NUMPAD) {
      if (DIRECTION_TO_NUMPAD[k] === trigger.dir) {
        parts.push(k);
        break;
      }
    }
  }
  const inv = { lp: '1', hp: '2', lk: '3', hk: '4', sp: '5', guard: 'g' };
  for (const b of trigger.buttons) parts.push(inv[b]);
  return parts.join('+');
}

/**
 * Builds the command table for one fighter.
 *
 * Starters and follow-ups are indexed separately: a follow-up is only reachable through its
 * parent's cancel list, which is what stops every move cancelling into every other move
 * (explicitly forbidden — combat must have commitment).
 *
 * @param {{ byId: Map<string,object>, all: Array<object> }} table
 * @param {string} prefix fighter move-id prefix, e.g. 'ky'
 */
export function buildCommandTable(table, prefix) {
  const starters = [];
  const byId = new Map();

  for (const move of table.all) {
    if (move.fighterPrefix !== prefix) continue;
    const trigger = parseInput(move.input, move.id);
    const entry = { move, trigger };
    byId.set(move.id, entry);
    if (!move.followUpOf) starters.push(entry);
  }

  // Most specific first, then slower startup last so a shared command favours the faster option
  // (pressing HP at point blank should give you the quick punish, not the slow launcher).
  starters.sort((a, b) => b.trigger.specificity - a.trigger.specificity || a.move.startup - b.move.startup);

  return { starters, byId, prefix };
}

/**
 * Does the snapshot satisfy this trigger right now?
 *
 * Button presses are matched against the input buffer rather than only the current tick, which
 * is what makes an input entered slightly early still come out. Directions are matched against
 * the currently held direction, because holding forward is a state, not an event.
 *
 * @param {object} snap InputSnapshot
 * @param {Trigger} trigger
 * @param {number} bufferTicks how far back a button press may be
 * @returns {boolean}
 */
export function triggerMatches(snap, trigger, bufferTicks) {
  if (trigger.buttons.length === 0 && !trigger.motion) return false;

  // --- motion ---
  if (trigger.motion) {
    if (snap.command !== trigger.motion) return false;
    if (snap.commandAge > COMMAND_WINDOW_TICKS) return false;
  }

  // --- direction ---
  if (trigger.dirRequired && snap.dir !== trigger.dir) {
    // Allow the diagonal that contains the required cardinal: holding down-forward should
    // still produce a `d`-prefixed move, which is how players actually input crouch attacks.
    if (!directionSatisfies(snap.dir, trigger.dir)) return false;
  }
  if (!trigger.dirRequired && trigger.dir === 5 && !trigger.motion) {
    // A neutral-notation move must not fire while a direction that owns its own move is held.
    // The specificity sort already prefers the directional variant, so nothing extra is needed
    // here; a bare button while holding forward simply loses to `f+button` if that exists.
  }

  // --- buttons: every required button must have a fresh press inside the buffer window ---
  for (let i = 0; i < trigger.buttons.length; i++) {
    if (!buttonPressedWithin(snap, trigger.buttons[i], bufferTicks)) return false;
  }

  // Simultaneous multi-button commands (throws) additionally require the presses to be close
  // together, otherwise LP then LK several frames later would register as a throw.
  if (trigger.buttons.length > 1 && !simultaneousWithin(snap, trigger.buttons, 4)) return false;

  return true;
}

/** True when `held` is a diagonal that includes the cardinal `required`. */
function directionSatisfies(held, required) {
  switch (required) {
    case 6: return held === 3 || held === 9;
    case 4: return held === 1 || held === 7;
    case 2: return held === 1 || held === 3;
    case 8: return held === 7 || held === 9;
    default: return false;
  }
}

const PRESS_FIELD = Object.freeze({
  lp: 'pressedLp', hp: 'pressedHp', lk: 'pressedLk', hk: 'pressedHk',
  sp: 'pressedSp', guard: 'pressedGuard',
});
const HELD_FIELD = Object.freeze({
  lp: 'heldLp', hp: 'heldHp', lk: 'heldLk', hk: 'heldHk', sp: 'heldSp', guard: 'heldGuard',
});

/**
 * Fresh press within `bufferTicks`. Uses the explicit edge flag for this tick and the held
 * counter for earlier ticks, which avoids walking the bitmask ring on the hot path.
 */
export function buttonPressedWithin(snap, button, bufferTicks) {
  if (snap[PRESS_FIELD[button]]) return true;
  const held = snap[HELD_FIELD[button]];
  const limit = Math.min(bufferTicks, INPUT_BUFFER_TICKS);
  return held > 0 && held <= limit;
}

/** Were all these buttons pressed within `window` ticks of each other? */
function simultaneousWithin(snap, buttons, window) {
  let min = Infinity;
  let max = 0;
  for (let i = 0; i < buttons.length; i++) {
    const held = snap[HELD_FIELD[buttons[i]]];
    // A press registered this tick has held === 1 after the input system's update.
    const age = snap[PRESS_FIELD[buttons[i]]] ? 0 : held;
    if (age < min) min = age;
    if (age > max) max = age;
  }
  return max - min <= window;
}

/**
 * Finds the best move for the current input, given what the fighter is allowed to do.
 *
 * @param {object} cmdTable from buildCommandTable
 * @param {object} snap InputSnapshot
 * @param {object} opts
 * @param {string[]|null} opts.candidates restrict to these move ids (a cancel list); null = starters
 * @param {string} opts.stance current stance, '' for none
 * @param {boolean} opts.crouching
 * @param {boolean} opts.airborne
 * @param {number} opts.bufferTicks
 * @param {number} opts.meter available meter, for meterCost gating
 * @returns {object|null} the matched move definition
 */
export function findMove(cmdTable, snap, opts) {
  const bufferTicks = opts.bufferTicks;
  let best = null;
  let bestSpec = -1;

  const list = opts.candidates
    ? mapCandidates(cmdTable, opts.candidates)
    : cmdTable.starters;

  for (let i = 0; i < list.length; i++) {
    const entry = list[i];
    if (!entry) continue;
    const move = entry.move;
    if (move.stanceRequired && move.stanceRequired !== opts.stance) continue;
    if (move.meterCost > 0 && opts.meter < move.meterCost) continue;
    if (move.airborneAttacker && !opts.airborne) continue;
    if (!move.airborneAttacker && opts.airborne) continue;
    if (!triggerMatches(snap, entry.trigger, bufferTicks)) continue;
    if (entry.trigger.specificity > bestSpec) {
      bestSpec = entry.trigger.specificity;
      best = move;
    }
  }
  return best;
}

/** Scratch array reused by findMove so cancel lookups never allocate. */
const _candidateScratch = new Array(24);

function mapCandidates(cmdTable, ids) {
  const n = Math.min(ids.length, _candidateScratch.length);
  for (let i = 0; i < n; i++) _candidateScratch[i] = cmdTable.byId.get(ids[i]) || null;
  for (let i = n; i < _candidateScratch.length; i++) _candidateScratch[i] = null;
  _candidateScratch.length = _candidateScratch.length; // keep length stable
  return _candidateScratch;
}
